import type { QueryClient } from '@tanstack/react-query';
import { voiceUUID } from './voice-contract';
import { captureVoiceOwner, isCurrentVoiceOwner, type VoiceSession } from './voice-owner';
import { reconcileVoice } from './queries';
const object = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);
const types = new Set(['voice:participant_joined', 'voice:participant_left', 'voice:channel_occupancy']);
export function readVoiceHint(value: unknown, owner: Pick<VoiceSession,'user'|'tenant'>): { channel: string; event: string } | null {
  if (!voiceUUID(owner.user) || !voiceUUID(owner.tenant) || !object(value) || typeof value.type !== 'string' || !types.has(value.type) || value.tenant_id !== owner.tenant || value.user_id !== owner.user || !object(value.data)) return null;
  const data = value.data;
  if (Object.keys(data).length !== 4 || data.version !== 1 || data.tenant_id !== owner.tenant || !voiceUUID(data.channel_id) || !voiceUUID(data.event_id)) return null;
  return { channel: data.channel_id, event: data.event_id };
}
interface Drain { dirty: boolean; running: boolean }
interface State { owner: VoiceSession; seen: Set<string>; channels: Map<string, Drain> }
const states = new WeakMap<QueryClient, State>();
export function receiveVoiceHint(qc: QueryClient, value: unknown): boolean {
  const owner = captureVoiceOwner(), hint = readVoiceHint(value, owner); if (!hint) return false;
  let state = states.get(qc); if (!state || !isCurrentVoiceOwner(state.owner)) { state = { owner, seen: new Set(), channels: new Map() }; states.set(qc,state); }
  const key = hint.channel + ':' + hint.event;
  if (state.seen.has(key)) return true; state.seen.add(key);
  if (state.seen.size > 256) state.seen.delete(state.seen.values().next().value!);
  let drain = state.channels.get(hint.channel); if (!drain) { drain = { dirty: false, running: false }; state.channels.set(hint.channel,drain); }
  drain.dirty = true; if (drain.running) return true; drain.running = true;
  const owned = state, job = drain;
  void (async () => {
    try { while (job.dirty && isCurrentVoiceOwner(owned.owner)) { job.dirty = false; try { await reconcileVoice(qc,hint.channel,captureVoiceOwner()); } catch { /* Foreground poll/focus/reconnect recover the owned read. */ } } }
    finally { job.running = false; if (owned.channels.get(hint.channel) === job) owned.channels.delete(hint.channel); }
  })();
  return true;
}
